import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { T } from "@/lib/theme";

// F-119: humanise raw Pydantic validation errors into copy a user can act on.
function _humanValidationError(x) {
  if (!x) return "Please check your details.";
  const msg = (x && (x.msg || x.message || x)) || "";
  const loc = Array.isArray(x?.loc) ? x.loc[x.loc.length - 1] : "";
  const raw = String(msg).toLowerCase();
  if (raw.includes("at least 8 characters") || raw.includes("min_length")) {
    return "Please choose a password of at least 8 characters.";
  }
  if (raw.includes("value is not a valid email") || raw.includes("email")) {
    return "That doesn't look like a valid email address.";
  }
  if (raw.includes("field required")) {
    return `Please fill in your ${loc || "details"}.`;
  }
  if (typeof msg === "string" && msg.trim()) return msg.replace(/^string\s+/i, "").trim();
  return "Please check your details.";
}

export default function Login({ mode: initMode = "login" }) {
  const [mode, setMode] = useState(initMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [org, setOrg] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // MFA challenge
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // F-119: honour `?next=` so users land back where they intended to go.
  // Guarded to same-origin relative paths — never redirect to an off-site URL.
  const nextUrl = (() => {
    const raw = params.get("next") || "";
    if (raw && raw.startsWith("/") && !raw.startsWith("//")) return raw;
    return null;
  })();

  const submit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setError("");
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      // Unified signup: everyone starts as "advertiser" (the most flexible default —
      // grants Plan, Map, Listen, AdVerify). Station owners convert via /claim,
      // which auto-upgrades them to station_manager on first successful claim.
      const body = mode === "login"
        ? (mfaRequired ? { email, password, mfa_code: mfaCode } : { email, password })
        : { email, password, role: "advertiser", organisation: org || name, full_name: name || org };
      const { data } = await api.post(url, body);
      if (data?.mfa_required) {
        setMfaRequired(true);
        setError("");
        setLoading(false);
        return;
      }
      login(data);
      if (mode === "register") {
        toast.success("Welcome to WaveScorePro 🎧", { description: "Your account is ready." });
      } else {
        toast.success(`Signed in${data.user?.full_name ? ` — welcome back, ${data.user.full_name.split(" ")[0]}` : ""}`);
      }
      // Route by role: station owners → dashboard, admins → admin console,
      // everyone else → live audience map (the most engaging landing).
      // F-119: if the user was redirected here from a gated URL, honour it.
      const r = data.user?.role;
      const isAdmin = !!(data.user?.admin_profile?.is_active
                         && (data.user?.admin_profile?.roles || []).length > 0);
      if (nextUrl) nav(nextUrl);
      else if (isAdmin) nav("/admin");
      else if (r === "station_manager") nav("/app/dashboard");
      else if (mode === "register") nav("/listen");
      else nav("/app/dashboard");
    } catch (e) {
      const d = e.response?.data?.detail;
      let msg;
      if (Array.isArray(d)) {
        msg = d.map((x) => _humanValidationError(x)).join(", ");
      } else if (d && typeof d === "object") {
        msg = _humanValidationError(d);
      } else {
        msg = _humanValidationError(d) || e.message || "Something went wrong";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const inp = {
    width: "100%", padding: "11px 14px", borderRadius: 8,
    border: `1px solid ${T.border}`, fontSize: 14, marginBottom: 14,
    outline: "none", fontFamily: "inherit", color: T.navy, background: "#fff",
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter',system-ui,sans-serif" }} data-testid="login-page">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap')`}</style>
      <div style={{ background: "#fff", borderRadius: 16, padding: "40px 36px", width: 440, boxShadow: "0 4px 40px rgba(0,0,0,0.08)" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.navy, marginBottom: 4 }}>
            WaveScore<span style={{ color: T.green }}>Pro</span>
          </div>
          <div style={{ fontSize: 13, color: T.muted }}>
            {mode === "register" ? "Create your free account · upgrade anytime" : "West Africa Audience Intelligence"}
          </div>
        </div>

        <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: `1px solid ${T.border}`, marginBottom: 24 }}>
          {[["login", "Sign in"], ["register", "Create account"]].map(([m, l]) => (
            <button key={m} data-testid={`mode-${m}`} onClick={() => setMode(m)}
              style={{ flex: 1, padding: "10px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, background: mode === m ? T.navy : "#fff", color: mode === m ? "#fff" : T.muted }}>
              {l}
            </button>
          ))}
        </div>

        {mode === "register" && (
          <>
            <input data-testid="input-name" style={inp} placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)} />
            <input data-testid="input-org" style={inp} placeholder="Company or organisation (optional)" value={org} onChange={(e) => setOrg(e.target.value)} />
          </>
        )}

        <input data-testid="input-email" style={inp} type="email" placeholder="Email address"
          value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
        <input data-testid="input-password" style={inp} type="password" placeholder="Password (min 8 characters)"
          value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />

        {mfaRequired && (
          <>
            <div data-testid="mfa-banner" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0",
              color: T.green, fontSize: 12, padding: "10px 14px", borderRadius: 8, marginBottom: 12, lineHeight: 1.5 }}>
              🔐 MFA enabled on this account. Enter the 6-digit code from your authenticator app.
            </div>
            <input data-testid="input-mfa-code" inputMode="numeric" maxLength={6}
              style={{ ...inp, fontFamily: "'DM Mono',monospace", fontSize: 20, letterSpacing: "0.4em", textAlign: "center" }}
              placeholder="••••••" value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && submit()} />
          </>
        )}

        {error && <div data-testid="auth-error" style={{ color: "#EF4444", fontSize: 13, marginBottom: 12 }}>⚠ {error}</div>}

        <button data-testid="submit-auth" type="submit" onClick={submit} disabled={loading}
          style={{
            width: "100%", padding: "12px", borderRadius: 8, border: "none",
            background: loading ? "#9CA3AF" : T.green, color: "#fff", fontSize: 15,
            fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit",
          }}
        >
          {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
        </button>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0 12px", color: T.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em" }}>
          <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
          <span>or</span>
          <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
        </div>

        {/* Google sign-in — Emergent-managed OAuth
           REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
        <button
          type="button"
          data-testid="google-signin-btn"
          onClick={() => {
            const redirectUrl = window.location.origin + '/dashboard';
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          style={{
            width: "100%", padding: "12px", borderRadius: 8,
            background: "#FFFFFF", color: "#1F2937",
            border: "1px solid #E5E7EB",
            fontSize: 14, fontWeight: 600,
            cursor: "pointer", fontFamily: "inherit",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Sign in with Google
        </button>

        <p style={{ textAlign: "center", fontSize: 12, color: T.muted, marginTop: 16 }}>
          {mode === "login" ? (
            <>
              <span data-testid="switch-register" onClick={() => setMode("register")} style={{ color: T.green, cursor: "pointer", fontWeight: 600 }}>Create an account</span>
              {" · "}
              <Link to="/forgot-password" data-testid="forgot-link" style={{ color: T.muted, fontWeight: 500 }}>Forgot password?</Link>
            </>
          ) : (
            <span>Already have an account? <span data-testid="switch-login" onClick={() => setMode("login")} style={{ color: T.green, cursor: "pointer", fontWeight: 600 }}>Sign in</span></span>
          )}
        </p>

        {mode === "register" && (
          <div data-testid="register-station-hint" style={{ marginTop: 18, padding: "12px 14px",
            background: "#F0FDF4", border: `1px solid #BBF7D0`, borderRadius: 10,
            fontSize: 11.5, color: T.navy, lineHeight: 1.55 }}>
            <strong style={{ color: T.green }}>📻 Run a radio or TV station?</strong>{" "}
            Sign up here — then claim your station inside the app to unlock the Station Dashboard.
            Pricing tiers (Starter / Pro / Agency / Brand) unlock automatically when you upgrade in
            <Link to="/billing" style={{ color: T.green, textDecoration: "none", fontWeight: 700 }}> Billing</Link>.
          </div>
        )}
      </div>
    </div>
  );
}
